const LESSON_TYPES_SETTING_ID = "lessonTypes";
const LESSON_TYPE_CATEGORIES = ["G", "I", "D", "T", "O"];
const LESSON_TYPE_STATUSES = ["ACTIVE", "INACTIVE"];

const DEFAULT_LESSON_TYPES = {
  id: LESSON_TYPES_SETTING_ID,
  types: [
    { classTypeId: "G45", category: "G", name: "Grupowe 45 min", durationMinutes: 45, minStudents: 4, maxStudents: 6, status: "ACTIVE" },
    { classTypeId: "G60", category: "G", name: "Grupowe 60 min", durationMinutes: 60, minStudents: 4, maxStudents: 6, status: "ACTIVE" },
    { classTypeId: "I45", category: "I", name: "Indywidualne 45 min", durationMinutes: 45, minStudents: 1, maxStudents: 1, status: "ACTIVE" },
    { classTypeId: "I60", category: "I", name: "Indywidualne 60 min", durationMinutes: 60, minStudents: 1, maxStudents: 1, status: "ACTIVE" },
    { classTypeId: "D45", category: "D", name: "Dwuosobowe 45 min", durationMinutes: 45, minStudents: 2, maxStudents: 2, status: "ACTIVE" },
    { classTypeId: "D60", category: "D", name: "Dwuosobowe 60 min", durationMinutes: 60, minStudents: 2, maxStudents: 2, status: "ACTIVE" },
    { classTypeId: "T45", category: "T", name: "Trzyosobowe 45 min", durationMinutes: 45, minStudents: 3, maxStudents: 3, status: "ACTIVE" },
    { classTypeId: "T60", category: "T", name: "Trzyosobowe 60 min", durationMinutes: 60, minStudents: 3, maxStudents: 3, status: "ACTIVE" },
    { classTypeId: "O45", category: "O", name: "Online 45 min", durationMinutes: 45, minStudents: 1, maxStudents: 1, status: "ACTIVE" },
    { classTypeId: "O60", category: "O", name: "Online 60 min", durationMinutes: 60, minStudents: 1, maxStudents: 1, status: "ACTIVE" },
  ],
};

function generateClassTypeId(category, durationMinutes) {
  const normalizedCategory = String(category || "").trim().toUpperCase();
  const normalizedDuration = Number(durationMinutes);
  if (!LESSON_TYPE_CATEGORIES.includes(normalizedCategory)) throw new Error(`Unknown lesson type category: ${category}`);
  if (!Number.isInteger(normalizedDuration) || normalizedDuration <= 0) throw new Error("Lesson type duration must be a positive integer");
  return `${normalizedCategory}${normalizedDuration}`;
}

function normalizeStatus(type) {
  if (typeof type.status === "string") return type.status.trim().toUpperCase();
  return type.active === false ? "INACTIVE" : "ACTIVE";
}

function inferCategory(classTypeId, category) {
  const normalizedCategory = String(category || "").trim().toUpperCase();
  if (normalizedCategory) return normalizedCategory;
  return String(classTypeId || "").trim().charAt(0).toUpperCase();
}

function normalizePositiveInteger(value, fallback) {
  const parsed = Number(value);
  if (Number.isInteger(parsed) && parsed > 0) return parsed;
  return fallback;
}

function normalizeLessonType(type = {}) {
  const classTypeId = String(type.classTypeId || type.id || "").trim().toUpperCase();
  const durationMinutes = normalizePositiveInteger(type.durationMinutes || type.length, null);
  const category = inferCategory(classTypeId, type.category);
  const status = normalizeStatus(type);
  const name = String(type.name || "").trim();
  const minStudents = normalizePositiveInteger(type.minStudents, 1);
  const maxStudents = normalizePositiveInteger(type.maxStudents, minStudents);

  if (!classTypeId) throw new Error("Lesson type code is required");
  if (!LESSON_TYPE_CATEGORIES.includes(category)) throw new Error(`Unknown lesson type category: ${category}`);
  if (!durationMinutes) throw new Error(`Invalid lesson type duration for ${classTypeId}`);
  if (classTypeId !== generateClassTypeId(category, durationMinutes)) {
    throw new Error(`Lesson type code must match category and duration for ${classTypeId}`);
  }
  if (!name) throw new Error(`Lesson type name is required for ${classTypeId}`);
  if (maxStudents < minStudents) throw new Error(`Invalid maxStudents for ${classTypeId}`);
  if (!LESSON_TYPE_STATUSES.includes(status)) throw new Error(`Invalid lesson type status for ${classTypeId}`);

  return { classTypeId, category, name, durationMinutes, minStudents, maxStudents, status };
}

function normalizeLessonTypesSetting(setting = {}) {
  const hasExplicitTypes = Object.prototype.hasOwnProperty.call(setting, "types");
  if (hasExplicitTypes && (!Array.isArray(setting.types) || setting.types.length === 0)) {
    throw new Error("Lesson types must contain at least one type");
  }

  const sourceTypes = hasExplicitTypes ? setting.types : DEFAULT_LESSON_TYPES.types;
  const seen = new Set();
  const types = sourceTypes.map(normalizeLessonType);
  types.forEach((type) => {
    if (seen.has(type.classTypeId)) throw new Error(`Duplicate lesson type code: ${type.classTypeId}`);
    seen.add(type.classTypeId);
  });

  return {
    ...setting,
    id: LESSON_TYPES_SETTING_ID,
    types,
  };
}

function getLessonTypeById(setting, classTypeId) {
  const normalizedId = String(classTypeId || "").trim().toUpperCase();
  const normalizedSetting = normalizeLessonTypesSetting(setting);
  return normalizedSetting.types.find(type => type.classTypeId === normalizedId) || {
    classTypeId: normalizedId,
    category: inferCategory(normalizedId),
    name: normalizedId,
    durationMinutes: undefined,
    minStudents: undefined,
    maxStudents: undefined,
    status: "INACTIVE",
  };
}

function getLessonTypeClassification(type = {}) {
  const category = inferCategory(type.classTypeId, type.category);
  return {
    category,
    isOnline: category === "O",
    isGroup: category === "G" || category === "T",
    isIndividual: category === "I" || category === "O",
    isPair: category === "D",
    countsAsGroupReport: category === "G" || category === "D" || category === "T",
  };
}

function getSelectableLessonTypes(setting, selectedClassTypeId) {
  const normalizedSetting = normalizeLessonTypesSetting(setting);
  const selectedId = String(selectedClassTypeId || "").trim().toUpperCase();
  return normalizedSetting.types.filter(type => type.status === "ACTIVE" || type.classTypeId === selectedId);
}

function toLegacyClassTypeOption(type) {
  return {
    ...type,
    id: type.classTypeId,
    length: type.durationMinutes,
    active: type.status === "ACTIVE",
  };
}

module.exports = {
  LESSON_TYPES_SETTING_ID,
  LESSON_TYPE_CATEGORIES,
  LESSON_TYPE_STATUSES,
  DEFAULT_LESSON_TYPES,
  generateClassTypeId,
  normalizeLessonTypesSetting,
  getLessonTypeById,
  getLessonTypeClassification,
  getSelectableLessonTypes,
  toLegacyClassTypeOption,
};
